import { HttpClient, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { Component, Injectable } from '@angular/core';
import { EndPointApi } from '@helpers/endpointapi';
import { Overlay } from '@angular/cdk/overlay';
import { lastValueFrom } from 'rxjs';
import { ComponentPortal } from '@angular/cdk/portal';
import { TableLoadingComponent } from '@components/table-loading/table-loading.component';
import { BlobVersionSelectorComponent } from '@components/blob-version-selector/blob-version-selector.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AzureLoginPromptComponent } from '@components/azure-login-prompt/azure-login-prompt.component';
import { Vessel } from 'ops/common/models/vessel';

@Injectable({ providedIn: 'root' })
export class AzureStorageService {
    private domain: string;
    private headers: any = new HttpHeaders({
        'Content-Type': 'application/json',
    });

    public overlayRef: any = this.overlay.create({
        hasBackdrop: true,
        positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
    });

    constructor(
        public dialog: MatDialog,
        private http: HttpClient,
        private overlay: Overlay,
    ) {
        this.domain = EndPointApi.iApiDomain();
    }

    getProjectFromBlob(blobData): Promise<any> {
        return lastValueFrom(
            this.http.get(`${this.domain}/api/blobStorage/pullProjectBlob`, {
                headers: this.headers,
                params: blobData,
            }),
        );
    }

    async openVersionList(vessel: Vessel) {
        if (!this.overlayRef.hasAttached()) {
            this.overlayRef.attach(new ComponentPortal(TableLoadingComponent));
        }
        try {
            const builderId = vessel.iAPIShipyardId;
            const vesselId = vessel.iAPIVesselId;
            const versionList = await this.getVersionList(builderId, vesselId);
            this.overlayRef.detach();
            if (versionList.length == 0) {
                this.dialog.open(EmptyBlobStorageErrorComponent);
                return;
            }
            const dialogRef = this.dialog.open(BlobVersionSelectorComponent, {
                data: {
                    versionList: versionList,
                    builderName: vessel.shipyardName,
                    vesselName: vessel.name,
                },
            });

            const result = await lastValueFrom(dialogRef.afterClosed());
            return result;
        } catch (e) {
            if (e.status === HttpStatusCode.Unauthorized) {
                this.overlayRef.detach();
                this.dialog.open(AzureLoginPromptComponent);
            } else {
                this.overlayRef.detach();
                return;
            }
        }
    }

    getVersionList(builderId, vesselId): Promise<any> {
        const params = {
            builderId: builderId,
            vesselId: vesselId,
        };
        return lastValueFrom(
            this.http.get(`${this.domain}/api/blobStorage/versionList`, {
                headers: this.headers,
                params: params,
            }),
        );
    }
}

@Component({
    selector: 'app-azure-login-prompt',
    template: `<div class="dialog-wrapper">
        <h2>{{ 'project-settings.error.empty-blob.title' | translate }}</h2>
        <div class="button-wrapper">
            <button class="component-large primary caption" (click)="this.dialogRef.close()">
                {{ 'project-settings.error.empty-blob.continue' | translate }}
            </button>
        </div>
    </div>`,
    styleUrls: ['./../../components/azure-login-prompt/azure-login-prompt.component.scss'],
    standalone: false
})
export class EmptyBlobStorageErrorComponent {
    constructor(public dialogRef: MatDialogRef<EmptyBlobStorageErrorComponent>) {}
}
